<template>
  <section class="mt-6 bg-white border border-gray-200 rounded-xl shadow-sm">
    <div class="px-5 py-4 border-b border-gray-100">
      <h3 class="font-semibold text-[#22282F]">Exceptions fines par direction, ambassade ou utilisateur</h3>
      <p class="text-sm text-gray-500 mt-1">
        Pour une direction, une ambassade ou une personne, choisissez <strong>exactement</strong> les types de document
        qu'elle voit en rédaction. Cette liste remplace la règle générale ci-dessus. En cas de conflit :
        <strong>utilisateur &gt; ambassade ou direction &gt; règle générale</strong>.
      </p>
    </div>

    <div v-if="loading" class="flex justify-center py-10">
      <div class="w-6 h-6 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
    </div>

    <div v-else class="p-5 space-y-4">
      <p v-if="!rows.length" class="text-xs text-gray-400 italic">Aucune exception : la règle générale s'applique à tous.</p>

      <div v-for="(row, i) in rows" :key="row.key" class="border border-gray-200 rounded-lg">
        <div class="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-t-lg">
          <span class="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 shrink-0">{{ KIND_LABEL[row.kind] }}</span>
          <span class="text-sm font-medium text-gray-800 flex-1 min-w-0 truncate">{{ row.nom }}</span>
          <span class="text-xs text-gray-500 shrink-0">{{ row.typeIds.length }} / {{ types.length }} types</span>
          <button type="button" class="text-xs text-gray-500 hover:text-gray-800 shrink-0" @click="setAll(row, true)">Tout</button>
          <button type="button" class="text-xs text-gray-500 hover:text-gray-800 shrink-0" @click="setAll(row, false)">Aucun</button>
          <button
            type="button"
            class="text-gray-400 hover:text-red-600 text-sm px-1"
            title="Retirer l'exception"
            @click="rows.splice(i, 1)"
          >✕</button>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 px-4 py-3">
          <label v-for="t in types" :key="t.id" class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input v-model="row.typeIds" type="checkbox" :value="t.id" class="w-4 h-4 accent-[#22282F]" />
            <span class="truncate">{{ t.nom }}</span>
            <span v-if="t.scope" class="text-xs text-gray-400 shrink-0">({{ t.scope }})</span>
            <span v-if="!t.is_active" class="text-xs text-amber-600 shrink-0">inactif</span>
          </label>
        </div>
        <p v-if="!row.typeIds.length" class="px-4 pb-3 text-xs text-amber-600">
          Aucun type coché : cette cible ne verra aucun type de document en rédaction.
        </p>
      </div>

      <!-- Ajout -->
      <div class="flex flex-col sm:flex-row gap-2">
        <select
          v-model="newKind"
          class="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]"
          @change="newTarget = ''; userQuery = ''"
        >
          <option value="direction">Une direction</option>
          <option value="ambassade">Une ambassade</option>
          <option value="user">Un utilisateur</option>
        </select>
        <input
          v-if="newKind === 'user'"
          v-model="userQuery"
          type="text"
          placeholder="Rechercher un nom…"
          class="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F]"
        />
        <select
          v-model="newTarget"
          class="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]"
        >
          <option value="">Choisir…</option>
          <option v-for="c in candidates" :key="c.id" :value="c.id">{{ c.nom }}</option>
        </select>
        <button
          type="button"
          :disabled="!newTarget"
          class="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40"
          @click="addRow"
        >Ajouter</button>
      </div>

      <div class="flex justify-end">
        <button
          type="button"
          :disabled="saving"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 disabled:opacity-50"
          @click="save"
        >{{ saving ? 'Enregistrement…' : 'Enregistrer les exceptions' }}</button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

type Kind = 'direction' | 'ambassade' | 'user'
interface Candidate { id: number; nom: string }
interface DocType {
  id: number
  nom: string
  is_active: boolean
  direction: { nom: string } | null
  ambassade: { nom: string } | null
  scope: string
}
interface ApiException { kind: Kind; id: number; nom: string; document_type_ids: number[] }
interface Row { key: string; kind: Kind; id: number; nom: string; typeIds: number[] }

const KIND_LABEL: Record<Kind, string> = { direction: 'Direction', ambassade: 'Ambassade', user: 'Utilisateur' }
const MAX_CANDIDATES = 50

const toast = useToast()
const loading = ref(true)
const saving = ref(false)
const rows = ref<Row[]>([])
const types = ref<DocType[]>([])
const directions = ref<Candidate[]>([])
const ambassades = ref<Candidate[]>([])
const users = ref<Candidate[]>([])

const newKind = ref<Kind>('direction')
const newTarget = ref<number | ''>('')
const userQuery = ref('')

const candidates = computed<Candidate[]>(() => {
  const source = { direction: directions.value, ambassade: ambassades.value, user: users.value }[newKind.value]
  const taken = new Set(rows.value.filter(r => r.kind === newKind.value).map(r => r.id))
  let list = source.filter(c => !taken.has(c.id))
  if (newKind.value === 'user') {
    const q = userQuery.value.trim().toLowerCase()
    if (q) list = list.filter(c => c.nom.toLowerCase().includes(q))
    list = list.slice(0, MAX_CANDIDATES)
  }
  return list
})

const toRows = (list: ApiException[]): Row[] =>
  list.map(e => ({ key: `${e.kind}${e.id}`, kind: e.kind, id: e.id, nom: e.nom, typeIds: [...e.document_type_ids] }))

const setAll = (row: Row, on: boolean) => {
  row.typeIds = on ? types.value.map(t => t.id) : []
}

const addRow = () => {
  if (!newTarget.value) return
  const source = { direction: directions.value, ambassade: ambassades.value, user: users.value }[newKind.value]
  const target = source.find(c => c.id === newTarget.value)
  if (!target) return
  // Liste blanche : on part de « tout coché » pour ne rien masquer par accident ;
  // l'admin décoche ce qu'il veut retirer.
  rows.value.push({
    key: `${newKind.value}${target.id}`,
    kind: newKind.value,
    id: target.id,
    nom: target.nom,
    typeIds: types.value.filter(t => t.is_active).map(t => t.id),
  })
  newTarget.value = ''
  userQuery.value = ''
}

const save = async () => {
  saving.value = true
  try {
    const payload = await apiFetch<{ exceptions: ApiException[] }>('/api/admin/document-types/access-exceptions/', {
      method: 'PUT',
      body: JSON.stringify({
        exceptions: rows.value.map(r => ({ kind: r.kind, id: r.id, document_type_ids: r.typeIds })),
      }),
    })
    rows.value = toRows(payload.exceptions)
    toast.success('Exceptions enregistrées', "Elles s'appliquent dès la prochaine rédaction")
  } catch (e: any) {
    toast.error('Erreur', e.message || "Impossible d'enregistrer les exceptions")
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    const [payload, docTypes, dirs, amb, usrs] = await Promise.all([
      apiFetch<{ exceptions: ApiException[] }>('/api/admin/document-types/access-exceptions/'),
      apiFetch<any[]>('/api/admin/document-types/'),
      apiFetch<Candidate[]>('/api/admin/directions/'),
      apiFetch<Candidate[]>('/api/admin/ambassades/'),
      apiFetch<any[]>('/api/admin/users/'),
    ])
    types.value = docTypes.map(t => ({
      id: t.id,
      nom: t.nom,
      is_active: t.is_active,
      direction: t.direction,
      ambassade: t.ambassade,
      scope: t.direction?.nom || t.ambassade?.nom || '',
    }))
    directions.value = dirs
    ambassades.value = amb
    users.value = usrs
      .filter(u => u.is_active)
      .map(u => ({ id: u.id, nom: u.full_name || u.username }))
      .sort((a, b) => a.nom.localeCompare(b.nom))
    rows.value = toRows(payload.exceptions)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger les exceptions de visibilité')
  } finally {
    loading.value = false
  }
})
</script>
